import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import type { IGridVirtualizedSelectProps } from "./types";

export const getOtherDataSelected = <T,>(
  selectableDatas: T[],
  selectedItems: T[],
  getId: (t: T, index: number) => string
) => {
  const dataIds = {};
  for (let i = 0; i < selectableDatas.length; i++) {
    const currentData = selectableDatas[i];
    dataIds[getId(currentData, i)] = currentData;
  }

  return selectedItems.filter((d, i) => !dataIds[getId(d, i)]);
};

export const selectOrUnselectData = <T,>(
  selectableDatas: T[],
  selectedItems: T[],
  getId: (t: T, index: number) => string
): T[] => {
  const otherDataSelected = getOtherDataSelected(selectableDatas, selectedItems, getId);

  if (selectedItems.length - otherDataSelected.length === selectableDatas.length) {
    return [...otherDataSelected];
  }
  return [...selectableDatas, ...otherDataSelected];
};

export const useGridVirtualizedSelect = <DataGetId, Data extends DataGetId>(
  data: IGridVirtualizedSelectProps<DataGetId, Data>["data"],
  selectedItemsProps: IGridVirtualizedSelectProps<DataGetId, Data>["selectedItems"] | undefined,
  disabledItemsProps: IGridVirtualizedSelectProps<DataGetId, Data>["disabledItems"] | undefined,
  onChange: IGridVirtualizedSelectProps<DataGetId, Data>["onChange"],
  getId: IGridVirtualizedSelectProps<DataGetId, Data>["getId"]
) => {
  const [selectedItems, _setSelectedItems] = useState<Data[]>([]);
  const selectableDatas = useMemo(() => {
    if (!disabledItemsProps || !disabledItemsProps.length) {
      return data;
    }

    const disabledIds = {};
    for (let i = 0; i < disabledItemsProps.length; i++) {
      const currentData = disabledItemsProps[i];
      disabledIds[getId(currentData, i)] = currentData;
    }

    return data.filter((d, index) => !disabledIds[getId(d, index)]);
  }, [disabledItemsProps, data, getId]);

  const setSelectedItems: Dispatch<SetStateAction<Data[]>> = useCallback(
    (value) => {
      _setSelectedItems((selectedItems) => {
        const nSelectedItems = typeof value === "function" ? value(selectedItems) : value;
        if (typeof onChange === "function" && nSelectedItems !== selectedItems) {
          onChange(nSelectedItems);
        }
        return nSelectedItems;
      });
    },
    [onChange]
  );

  const handleSelectAll = useCallback(() => {
    setSelectedItems((selectedItems) => selectOrUnselectData(selectableDatas, selectedItems, getId));
  }, [selectableDatas, getId, setSelectedItems]);

  const handleSelect = useCallback(
    (item: Data, index: number) => {
      const id = getId(item, index);
      setSelectedItems((selectedItems) => {
        const index = selectedItems.findIndex((v, index) => getId(v, index) === id);
        if (index === -1) {
          return [...selectedItems, item];
        }

        const nSelectedItems = [...selectedItems];
        nSelectedItems.splice(index, 1);
        return nSelectedItems;
      });
    },
    [getId, setSelectedItems]
  );

  useEffect(() => {
    if (selectedItemsProps !== undefined) {
      _setSelectedItems(selectedItemsProps);
    }
  }, [selectedItemsProps]);

  const isChecked = useMemo(() => {
    if (!selectableDatas.length) {
      return false;
    }

    const otherDataSelected = getOtherDataSelected(selectableDatas, selectedItems, getId);

    return selectedItems.length - otherDataSelected.length === selectableDatas.length;
  }, [selectableDatas, selectedItems, getId]);

  const isIndeterminate = useMemo(() => {
    const otherDataSelected = getOtherDataSelected(selectableDatas, selectedItems, getId);
    const dataSelectedCount = selectedItems.length - otherDataSelected.length;

    return dataSelectedCount !== selectableDatas.length && dataSelectedCount > 0;
  }, [selectableDatas, selectedItems, getId]);

  return { selectedItems, isChecked, isIndeterminate, setSelectedItems, handleSelectAll, handleSelect };
};
